/**
 * Country-code catalogue for phone inputs that accept non-Saudi numbers (e.g.
 * the public "register interest" form). The list + per-country national-number
 * rules live in ONE place — `apps/shared/src/phone-countries.ts` — shared with
 * the API's `IsSupportedMobile` validator (mirrored + parity-pinned there).
 * This module re-exports it so existing `@/lib/phoneCountries` imports keep
 * working.
 *
 * NOTE: login/OTP surfaces stay Saudi-only (the API's `IsSaudiPhone` validator
 * on /auth/* only accepts +9665XXXXXXXX) — they keep using `PhoneInput`
 * without a `countries` list. Only the public interest capture's OTP
 * (POST /leads/public/request-otp) accepts every country listed here.
 */
import type { PhoneCountry } from "@dallali/shared";

export {
  PHONE_COUNTRIES,
  SAUDI_PHONE_COUNTRY,
  findPhoneCountryByDial,
  findPhoneCountryForE164,
  isValidNationalNumber,
  isValidE164ForCountries,
  toE164,
  type PhoneCountry,
  type PhoneCountryCode,
} from "@dallali/shared";

/**
 * Whatever the user typed or pasted into `PhoneInput`'s number box → the bare
 * national digits for `country`: non-digits dropped, a pasted dial code
 * (`+966…`, `00966…`, `966…`) and leading trunk `0`s stripped, capped at the
 * country's national length.
 */
export function toNationalDigits(raw: string, country: PhoneCountry): string {
  let digits = raw.replace(/\D/g, "");
  const dial = country.dialCode.replace(/\D/g, "");
  if (digits.startsWith(`00${dial}`)) {
    digits = digits.slice(2 + dial.length);
  } else if (
    digits.startsWith(dial) &&
    (raw.trimStart().startsWith("+") || digits.length > country.nationalLength)
  ) {
    digits = digits.slice(dial.length);
  }
  return digits.replace(/^0+/, "").slice(0, country.nationalLength);
}
